import { Modifier } from '@/types/menus.types';

const PLAIN_INACTIVE = 'Inactive';
const INACTIVE_PRICE_PREFIX = 'Inactive-';
const PLAIN_NUMERIC_PRICE = /^\d+(\.\d+)?$/;

/** Pizza categories where edit flow updates existing prices only (no activation on menus without a price). */
export const MODIFIER_CATEGORIES_PRICE_UPDATE_ONLY = new Set(['Pizza Cheese', 'Pizza Toppings', 'Pizza Topping']);

/** Categories that use modifier multi-select instead of modifier text search. */
export const MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT = new Set(['Other', 'Appetizer Topping']);

/** Categories that also require a subcategory (slot) dropdown before modifier selection. */
export const MODIFIER_CATEGORIES_WITH_SUBCATEGORY_FILTER = new Set(['Other']);

/** @deprecated Use MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT */
export const MODIFIER_CATEGORIES_WITH_SLOT_FILTERS = MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT;

export function isModifierCategoryPriceUpdateOnly(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_PRICE_UPDATE_ONLY.has(categoryName);
}

export function isModifierCategoryWithModifierMultiSelect(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_WITH_MODIFIER_MULTI_SELECT.has(categoryName);
}

export function isModifierCategoryWithSubcategoryFilter(categoryName: string): boolean {
  return MODIFIER_CATEGORIES_WITH_SUBCATEGORY_FILTER.has(categoryName);
}

export function isModifierCategoryWithSlotFilters(categoryName: string): boolean {
  return isModifierCategoryWithModifierMultiSelect(categoryName);
}

export type ModifierSlotFilterOption = {
  modifierId: string;
  modifierName: string;
};

export type ModifierSlotFilterSlot = {
  value: string;
  label: string;
  modifiers: ModifierSlotFilterOption[];
};

/** Flatten slot modifiers (deduped), sorted alphabetically by name — used for Appetizer Topping multi-select. */
export function flattenModifiersFromSlots(slots: ModifierSlotFilterSlot[]): ModifierSlotFilterOption[] {
  const byId = new Map<string, ModifierSlotFilterOption>();
  for (const slot of slots) {
    for (const modifier of slot.modifiers) {
      byId.set(modifier.modifierId, modifier);
    }
  }
  return [...byId.values()].sort((a, b) =>
    a.modifierName.localeCompare(b.modifierName, undefined, { sensitivity: 'base' }),
  );
}

/** Default selection: first modifier in flattened alphabetical order. */
export function getDefaultModifierIdsFromSlots(slots: ModifierSlotFilterSlot[]): string[] {
  const flat = flattenModifiersFromSlots(slots);
  return flat.length ? [flat[0].modifierId] : [];
}

/** Default subcategory (first slot, alphabetical by slot label). */
export function getDefaultSubcategorySlotId(slots: ModifierSlotFilterSlot[]): string {
  return slots[0]?.value ?? '';
}

/**
 * Default modifier id(s) for multi-select categories.
 * When subCategorySlotId is set (Other), uses only that slot's modifiers.
 */
export function getDefaultModifierIdsForCategory(
  slots: ModifierSlotFilterSlot[],
  subCategorySlotId?: string,
): string[] {
  if (subCategorySlotId) {
    const slot = slots.find((s) => s.value === subCategorySlotId);
    return slot ? getDefaultModifierIdsFromSlots([slot]) : [];
  }
  return getDefaultModifierIdsFromSlots(slots);
}

export function resolveSlotIdForModifierFetch(
  categoryName: string,
  slots: ModifierSlotFilterSlot[],
  subCategory: string | undefined,
  modifierIds: string[],
): string {
  if (isModifierCategoryWithSubcategoryFilter(categoryName)) {
    return subCategory ?? '';
  }
  if (!modifierIds.length) {
    return slots[0]?.value ?? '';
  }
  const slotForFirstSelected = slots.find((slot) =>
    slot.modifiers.some((modifier) => modifier.modifierId === modifierIds[0]),
  );
  if (slotForFirstSelected) {
    return slotForFirstSelected.value;
  }
  const slotForAnySelected = slots.find((slot) =>
    slot.modifiers.some((modifier) => modifierIds.includes(modifier.modifierId)),
  );
  return slotForAnySelected?.value ?? slots[0]?.value ?? '';
}

export function isZeroPricedModifierSelection(modifiers: Modifier[]): boolean {
  return modifiers.length > 0 && modifiers.every((modifier) => modifier.isZeroPriced === true);
}

export function normalizeChosenPriceRange(chosenPriceRange: string | undefined): string {
  if (chosenPriceRange == null) return '';
  return String(chosenPriceRange).trim();
}

/**
 * Fully inactive for visibility/hidden-row rules: empty or exact "Inactive" only.
 * Values like "Inactive-2.00" still count as having listing context.
 */
export function isChosenPriceFullyInactiveForVisibility(chosenPriceRange: string | undefined): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  return normalized === '' || normalized === PLAIN_INACTIVE;
}

export function hasActiveChosenPrice(chosenPriceRange: string | undefined): boolean {
  return !isChosenPriceFullyInactiveForVisibility(chosenPriceRange);
}

/** Products-style edit block: "Inactive-" prefix or plain numeric price (e.g. "10.69"). */
export function hasRelevantActivePriceInStoreGroup(chosenPriceRange: string | undefined): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  if (!normalized) return false;
  return normalized.startsWith(INACTIVE_PRICE_PREFIX) || PLAIN_NUMERIC_PRICE.test(normalized);
}

/** True when the store group has any existing price context (active, inactive-with-price, or range). */
export function hasExistingPriceInStoreGroup(chosenPriceRange: string | undefined): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  return normalized !== '' && normalized !== PLAIN_INACTIVE;
}

export function modifierHasExistingPriceInStoreGroup(
  modifier: Pick<Modifier, 'storeGroups'>,
  storeGroupName: string,
): boolean {
  return hasExistingPriceInStoreGroup(getChosenPriceForStoreGroup(modifier, storeGroupName));
}

export function getStoreGroupNamesWithExistingPrices(modifiers: Modifier[], storeGroupNames: string[]): string[] {
  return storeGroupNames.filter((storeGroupName) =>
    modifiers.some((modifier) => modifierHasExistingPriceInStoreGroup(modifier, storeGroupName)),
  );
}

/**
 * For price-update-only categories: stores in the user's selection that already have a price
 * for this modifier (from store groups with existing chosenPriceRange).
 */
export function getStoresWithExistingPricesForModifier(
  modifier: Pick<Modifier, 'storeGroups'>,
  selectedStoreGroupNames: string[],
  selectedStoreNumbers: string[],
): string[] {
  if (!selectedStoreGroupNames.length || !selectedStoreNumbers.length) {
    return [];
  }

  const selectedStoreSet = new Set(selectedStoreNumbers);
  const selectedGroupSet = new Set(selectedStoreGroupNames);
  const storesWithPrice = new Set<string>();

  for (const storeGroup of modifier.storeGroups) {
    if (!selectedGroupSet.has(storeGroup.storeGroupName)) {
      continue;
    }
    if (!hasExistingPriceInStoreGroup(storeGroup.chosenPriceRange)) {
      continue;
    }

    for (const storeNo of storeGroup.stores ?? []) {
      if (selectedStoreSet.has(storeNo)) {
        storesWithPrice.add(storeNo);
      }
    }
  }

  return Array.from(storesWithPrice);
}

function getChosenPriceForStoreGroup(
  modifier: Pick<Modifier, 'storeGroups'>,
  storeGroupName: string,
): string | undefined {
  return modifier.storeGroups.find((group) => group.storeGroupName === storeGroupName)?.chosenPriceRange;
}

function modifierHasStoreGroupMatching(
  modifier: Modifier,
  predicate: (chosenPriceRange: string | undefined) => boolean,
): boolean {
  return modifier.storeGroups.some((storeGroup) => predicate(storeGroup.chosenPriceRange));
}

/**
 * True if the modifier is active on the menu in at least one selected store group
 * (any value other than empty / plain "Inactive").
 */
export function modifierHasActiveListingInSelectedStores(
  modifier: Modifier,
  selectedStoreGroupNames: string[],
): boolean {
  if (!selectedStoreGroupNames.length) return false;
  return selectedStoreGroupNames.some((name) => hasActiveChosenPrice(getChosenPriceForStoreGroup(modifier, name)));
}

/** Hide row when isVisible is false and fully inactive in every selected store. */
export function shouldHideInvisibleModifier(
  modifier: Pick<Modifier, 'isVisible' | 'storeGroups'>,
  selectedStoreGroupNames: string[],
): boolean {
  if (modifier.isVisible !== false) return false;
  if (!selectedStoreGroupNames.length) return true;
  return selectedStoreGroupNames.every((name) =>
    isChosenPriceFullyInactiveForVisibility(getChosenPriceForStoreGroup(modifier, name)),
  );
}

/** Inactive in all selected store groups (only empty, missing, or plain "Inactive"). */
export function isModifierCompletelyInactiveAcrossSelection(
  modifier: Modifier,
  selectedStoreGroupNames: string[],
): boolean {
  if (!modifier?.storeGroups?.length) return true;
  if (!selectedStoreGroupNames.length) return true;
  return selectedStoreGroupNames.every((name) => {
    const storeGroup = modifier.storeGroups.find((group) => group.storeGroupName === name);
    if (!storeGroup) return true;
    return isChosenPriceFullyInactiveForVisibility(storeGroup.chosenPriceRange);
  });
}

function parsePriceNumbers(normalized: string): number[] {
  if (!normalized || normalized === PLAIN_INACTIVE) return [];
  if (normalized.startsWith(INACTIVE_PRICE_PREFIX)) {
    const value = parseFloat(normalized.slice(INACTIVE_PRICE_PREFIX.length).trim());
    return Number.isNaN(value) ? [0] : [value];
  }
  return normalized
    .split(/-/)
    .map((part) => parseFloat(part.trim()))
    .filter((value) => !Number.isNaN(value));
}

/** First numeric value in a chosenPriceRange, handling Inactive-X and min-max ranges. */
export function getSortValueFromModifierChosenPrice(chosenPriceRange: string | undefined): number {
  const numbers = parsePriceNumbers(normalizeChosenPriceRange(chosenPriceRange));
  return numbers.length > 0 ? numbers[0]! : Number.NaN;
}

/** Sort key for a modifier column, using min or max of a range based on sort direction. */
export function getModifierColumnSortValue(item: Modifier, columnId: string, currentSort: 'asc' | 'desc'): number {
  const raw = item.storeGroups.find((group) => group.storeGroupName === columnId)?.chosenPriceRange;
  const numbers = parsePriceNumbers(normalizeChosenPriceRange(raw));
  if (numbers.length === 0) return Number.NaN;
  if (numbers.length === 1) return numbers[0]!;
  return currentSort === 'desc' ? numbers[numbers.length - 1]! : numbers[0]!;
}

export function formatModifierPriceCell(
  chosenPriceRange: string | undefined,
  inactiveLabel: string,
): { text: string; showRangeInfo: boolean } {
  const raw = normalizeChosenPriceRange(chosenPriceRange);
  if (!raw || raw === PLAIN_INACTIVE) {
    return { text: inactiveLabel, showRangeInfo: false };
  }

  if (raw.startsWith(INACTIVE_PRICE_PREFIX)) {
    const price = raw.slice(INACTIVE_PRICE_PREFIX.length).trim();
    return { text: `${inactiveLabel} - $${price}`, showRangeInfo: true };
  }

  const formatted = `$${raw}`.replace(/-/g, ' - ');
  const hasRange = formatted.includes(' - ') || (raw.includes('-') && !raw.startsWith(INACTIVE_PRICE_PREFIX));
  return { text: formatted, showRangeInfo: hasRange };
}

export function modifierHasPriceLockRelevantState(modifier: Modifier): boolean {
  return Boolean(modifier.priceIsLocked) && modifierHasStoreGroupMatching(modifier, hasRelevantActivePriceInStoreGroup);
}

/** Invisible modifiers with an active price: edit/activate blocked; deactivate still allowed. */
export function modifierHasInvisibleActivePriceState(modifier: Modifier): boolean {
  return modifier.isVisible === false && modifierHasStoreGroupMatching(modifier, hasRelevantActivePriceInStoreGroup);
}

export function modifierBlocksEditActivation(modifier: Modifier): boolean {
  return modifierHasPriceLockRelevantState(modifier) || modifierHasInvisibleActivePriceState(modifier);
}

function parseTargetPriceForComparison(targetPrice: string): number | null {
  const sanitized = String(targetPrice ?? '').trim();
  if (!sanitized) return null;
  const value = parseFloat(sanitized);
  return Number.isNaN(value) ? null : value;
}

function pricesEqual(a: number, b: number): boolean {
  return Number(a.toFixed(2)) === Number(b.toFixed(2));
}

/** True when chosenPriceRange is a single effective price equal to the target (not a range or inactive). */
export function isChosenPriceEqualToTargetPrice(chosenPriceRange: string | undefined, targetPrice: number): boolean {
  const normalized = normalizeChosenPriceRange(chosenPriceRange);
  if (!normalized || normalized === PLAIN_INACTIVE) {
    return false;
  }

  const numbers = parsePriceNumbers(normalized);
  if (numbers.length === 0) return false;
  if (numbers.length === 1) {
    return pricesEqual(numbers[0]!, targetPrice);
  }

  return pricesEqual(numbers[0]!, targetPrice) && pricesEqual(numbers[numbers.length - 1]!, targetPrice);
}

/**
 * True when every compared store group on every modifier already has the target price
 * (no price change would result from submission).
 */
export function areAllModifierStoreGroupPricesEqualToTarget(
  modifiers: Modifier[],
  storeGroupNames: string[],
  targetPrice: string,
  options?: { priceUpdateOnly?: boolean },
): boolean {
  if (!modifiers.length || !storeGroupNames.length) {
    return false;
  }

  const target = parseTargetPriceForComparison(targetPrice);
  if (target === null) {
    return false;
  }

  return modifiers.every((modifier) =>
    storeGroupNames.every((storeGroupName) => {
      if (options?.priceUpdateOnly && !modifierHasExistingPriceInStoreGroup(modifier, storeGroupName)) {
        return true;
      }

      const chosenPriceRange = modifier.storeGroups?.find(
        (group) => group.storeGroupName === storeGroupName,
      )?.chosenPriceRange;

      return isChosenPriceEqualToTargetPrice(chosenPriceRange, target);
    }),
  );
}
